import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
} from '@mui/material';
import { FaPlus, FaSearch } from 'react-icons/fa';
import { meetingsApi } from '../../../../services/api/meetingsApi';
import { useAlert } from '../../../../contexts/AlertContext';
import EmptyState from '../../../common/EmptyState';
import LoadingSpinner from '../../../common/LoadingSpinner';
import ResultsCount from '../../../common/ResultsCount';
import ConfirmationModal from '../../../common/ConfirmationModal';
import Modal from '../../../common/Modal';
import { HeaderButton } from '../../../common/styles';
import SystemTalkingPointsModal from './SystemTalkingPointsModal';
import ConfigureTalkingPointCard from './ConfigureTalkingPointCard';

const ConfigureTalkingPointsTab = ({ meetingId, meeting, permissions, onChange }) => {
  const { showAlert } = useAlert();
  const [talkingPoints, setTalkingPoints] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [pointToRemove, setPointToRemove] = useState(null);
  const [showSystemModal, setShowSystemModal] = useState(false);
  const [showAddCustomModal, setShowAddCustomModal] = useState(false);
  const [newCustomQuestion, setNewCustomQuestion] = useState('');
  const [newCustomInstructions, setNewCustomInstructions] = useState('');

  useEffect(() => {
    fetchTalkingPoints();
  }, [meetingId]);

  const fetchTalkingPoints = async () => {
    try {
      setIsLoading(true);
      const points = await meetingsApi.getMeetingTalkingPoints(meetingId);
      setTalkingPoints(points || []);
    } catch (err) {
      showAlert('Failed to load talking points', 'error', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddSystemTalkingPoint = async (systemPoint) => {
    try {
      await meetingsApi.addMeetingTalkingPoint(meetingId, {
        talkingPointId: systemPoint.id,
        order: talkingPoints.length + 1,
      });
      showAlert('Talking point added', 'success');
      onChange?.();
      fetchTalkingPoints();
    } catch (err) {
      showAlert('Failed to add talking point', 'error', err);
    }
  };

  const handleAddCustomTalkingPoint = async () => {
    if (!newCustomQuestion.trim()) return;

    try {
      await meetingsApi.addMeetingTalkingPoint(meetingId, {
        question: newCustomQuestion.trim(),
        instructions: newCustomInstructions.trim() || undefined,
        order: talkingPoints.length + 1,
      });
      showAlert('Talking point added', 'success');
      onChange?.();
      handleCloseCustomModal();
      fetchTalkingPoints();
    } catch (err) {
      showAlert('Failed to add talking point', 'error', err);
    }
  };

  const handleCloseCustomModal = () => {
    setShowAddCustomModal(false);
    setNewCustomQuestion('');
    setNewCustomInstructions('');
  };

  const handleRemoveTalkingPoint = (point) => {
    setPointToRemove(point);
  };

  const handleConfirmRemoveTalkingPoint = async () => {
    if (!pointToRemove) return;
    try {
      await meetingsApi.removeMeetingTalkingPoint(meetingId, pointToRemove.id);
      showAlert('Talking point removed', 'success');
      onChange?.();
      fetchTalkingPoints();
    } catch (err) {
      showAlert('Failed to remove talking point', 'error', err);
    } finally {
      setPointToRemove(null);
    }
  };

  const handleCancelRemoveTalkingPoint = () => {
    setPointToRemove(null);
  };

  // Get meeting type IDs for system talking points modal
  const meetingTypeIds = meeting.meetingTypes?.map(t => t.meetingType?.id || t.id) || [];

  // Get IDs of already added system talking points
  const addedTalkingPointIds = talkingPoints
    .filter(tp => tp.talkingPoint?.id && tp.talkingPoint?.isSystemTalkingPoint)
    .map(tp => tp.talkingPoint.id);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* Add Talking Points Section */}
      {permissions.canAddTalkingPoints && (
        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
          <HeaderButton onClick={() => setShowSystemModal(true)}>
            <FaSearch /> Add System Talking Point
          </HeaderButton>
          <HeaderButton onClick={() => setShowAddCustomModal(true)}>
            <FaPlus /> Add Custom Talking Point
          </HeaderButton>
        </Box>
      )}

      {/* Talking Points Grid */}
      <Box>
        {!isLoading && talkingPoints.length > 0 && (
          <ResultsCount
            count={talkingPoints.length}
            singular="talking point"
            plural="talking points"
          />
        )}

        {isLoading ? (
          <LoadingSpinner />
        ) : talkingPoints.length === 0 ? (
          <EmptyState
            title="No talking points added yet"
            description="Add system or custom talking points to start building your meeting agenda."
          />
        ) : (
          <Box sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, 1fr)',
              md: 'repeat(3, 1fr)',
            },
            gap: 3,
            width: '100%',
          }}>
            {talkingPoints.map((point) => (
              <Box key={point.id}>
                <ConfigureTalkingPointCard
                  point={point}
                  onRemove={handleRemoveTalkingPoint}
                  permissions={permissions}
                />
              </Box>
            ))}
          </Box>
        )}
      </Box>

      {/* System Talking Points Modal */}
      <SystemTalkingPointsModal
        isOpen={showSystemModal}
        onClose={() => setShowSystemModal(false)}
        meetingTypeIds={meetingTypeIds}
        addedTalkingPointIds={addedTalkingPointIds}
        onAddTalkingPoint={handleAddSystemTalkingPoint}
      />

      {/* Remove Talking Point Confirmation Modal */}
      <ConfirmationModal
        isOpen={Boolean(pointToRemove)}
        onClose={handleCancelRemoveTalkingPoint}
        onConfirm={handleConfirmRemoveTalkingPoint}
        title="Remove Talking Point"
        message={`Are you sure you want to remove this talking point: "${pointToRemove?.talkingPoint?.question || pointToRemove?.question || ''}"?`}
        actionButtonText="Remove"
        actionButtonColor="#dc2626"
      />

      {/* Add Custom Talking Point Modal */}
      <Modal
        isOpen={showAddCustomModal}
        onClose={handleCloseCustomModal}
        onSubmit={(e) => {
          e.preventDefault();
          handleAddCustomTalkingPoint();
        }}
        title="Add Custom Talking Point"
        submitText="Save"
        cancelText="Cancel"
        submitDisabled={!newCustomQuestion.trim()}
        width="500px"
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          <TextField
            fullWidth
            label="Question"
            placeholder="Enter a custom question or topic..."
            value={newCustomQuestion}
            onChange={(e) => setNewCustomQuestion(e.target.value)}
            autoComplete="off"
            autoFocus
          />
          <TextField
            fullWidth
            label="Instructions (optional)"
            placeholder="Enter any instructions or context for this talking point..."
            value={newCustomInstructions}
            onChange={(e) => setNewCustomInstructions(e.target.value)}
            multiline
            rows={3}
            autoComplete="off"
          />
        </Box>
      </Modal>
    </Box>
  );
};

export default ConfigureTalkingPointsTab;
